import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { H3 } from '../../style/Typography';
import { Naked } from '../Buttons/Button';
import CheckBox from './Checkbox';
import { getParentState, hasHierarchy, toggleParent, toggleValue } from './checkbox-utils';
import * as S from './styled';

export const MultiCheckbox = ({
  options,
  label,
  onClear,
  clearLabel = 'Clear',
  showMoreLabel = 'Show More',
  showLessLabel = 'Show Less',
  limit = 5,
  onChange,
  value,
  testId
}) => {
  const [isShowMore, setIsShowMore] = useState(false);
  const isHierarchical = hasHierarchy(options);

  const toggleShowHideMore = () => {
    setIsShowMore(cur => !cur);
  };

  const changeHandler = e => {
    const itemVal = e.target.dataset?.value;
    onChange(value?.includes(itemVal) ? value.filter(v => v !== itemVal) : [...value, itemVal]);
  };

  const onToggleOption = option => () => onChange(toggleValue(option.value, value));
  const onToggleParent = option => () => onChange(toggleParent(option, value));

  // In hierarchical mode the limit counts top-level options; a shown parent always shows its children.
  const visibleOptions = isShowMore ? options : options.slice(0, limit);

  const renderOption = option => {
    if (!option.children?.length) {
      return (
        <CheckBox
          key={option.value}
          onChange={isHierarchical ? onToggleOption(option) : changeHandler}
          checked={!!value?.includes(option.value)}
          data-value={option.value}
          value={option.value}
          label={option.label}
          disabled={option.disabled}
          testId={option.testId || `${testId}-${option.value}`}
        />
      );
    }

    const { checked, indeterminate } = getParentState(option, value);

    return (
      <React.Fragment key={option.key || option.value}>
        <CheckBox
          onChange={onToggleParent(option)}
          checked={checked}
          indeterminate={indeterminate}
          label={option.label}
          disabled={option.disabled}
          testId={option.testId || `${testId}-${option.key || option.value}`}
        />
        <S.SubCheckboxesWrapper>{option.children.map(renderOption)}</S.SubCheckboxesWrapper>
      </React.Fragment>
    );
  };

  return (
    <S.MultiCheckboxWrapper>
      {(label || onClear) && (
        <S.MultiCheckboxHeader>
          <H3 weight="BOLD">{label}</H3>
          <S.ClearButtonWrapper $hidden={!onClear}>
            <Naked onClick={onClear}>{clearLabel || 'Clear'}</Naked>
          </S.ClearButtonWrapper>
        </S.MultiCheckboxHeader>
      )}
      <S.CheckboxesWrapper>{visibleOptions.map(renderOption)}</S.CheckboxesWrapper>
      {options?.length > limit && (
        <S.ExpandButtonWrapper>
          <Naked onClick={toggleShowHideMore}>{isShowMore ? showLessLabel : showMoreLabel}</Naked>
        </S.ExpandButtonWrapper>
      )}
    </S.MultiCheckboxWrapper>
  );
};

const optionValue = PropTypes.oneOfType([PropTypes.string, PropTypes.bool, PropTypes.number]);

MultiCheckbox.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.node.isRequired,
      value: optionValue,
      disabled: PropTypes.bool,
      key: PropTypes.string,
      testId: PropTypes.string,
      children: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.node.isRequired,
          value: optionValue.isRequired,
          disabled: PropTypes.bool,
          testId: PropTypes.string
        })
      )
    })
  ).isRequired,
  label: PropTypes.node,
  onClear: PropTypes.func,
  clearLabel: PropTypes.string,
  showMoreLabel: PropTypes.string,
  showLessLabel: PropTypes.string,
  limit: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.arrayOf(optionValue),
  testId: PropTypes.string
};

export default MultiCheckbox;
